'use client';

import { c as _c } from "react/compiler-runtime";
import { useRouter } from 'next/navigation';
import { use } from 'react';
import { AppContext } from '@/app/app-context';
import { Container } from '@/components/Container';
import { Prose } from '@/components/Prose';
import { type BlogWithSlug } from '@/lib/blogs';
import { formatDate } from '@/lib/formatDate';
import { ArrowLeftIcon } from '@/components/icons';
import { JsonLd } from '@/components/JsonLd';
import { breadcrumbSchema, personEntity } from '@/lib/schema';
import { getSiteUrl } from '@/lib/site';
export function BlogLayout(t0) {
  const $ = _c(45);
  const {
    blog,
    children
  } = t0;
  const {
    back
  } = useRouter();
  const {
    previousPathname
  } = use(AppContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getSiteUrl();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const siteUrl = t1;
  const pageUrl = `${siteUrl}/blog/${blog.slug || ""}`;
  let t2;
  if ($[1] !== pageUrl) {
    t2 = {
      "@type": "WebPage",
      "@id": pageUrl
    };
    $[1] = pageUrl;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [`${siteUrl}/opengraph-image`];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== blog.date || $[5] !== blog.description || $[6] !== blog.title || $[7] !== pageUrl || $[8] !== t2) {
    t4 = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      mainEntityOfPage: t2,
      url: pageUrl,
      headline: blog.title,
      description: blog.description,
      image: t3,
      author: personEntity,
      datePublished: blog.date,
      dateModified: blog.date,
      publisher: personEntity
    };
    $[4] = blog.date;
    $[5] = blog.description;
    $[6] = blog.title;
    $[7] = pageUrl;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const blogPostingSchema = t4;
  let t5;
  if ($[10] !== blogPostingSchema) {
    t5 = <JsonLd id="blog-posting-schema" data={blogPostingSchema} />;
    $[10] = blogPostingSchema;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== blog.slug || $[13] !== blog.title) {
    t6 = breadcrumbSchema([["Blog", "/blog"], [blog.title, `/blog/${blog.slug || ""}`]]);
    $[12] = blog.slug;
    $[13] = blog.title;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <JsonLd id="breadcrumb-schema" data={t6} />;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== back || $[18] !== previousPathname) {
    t8 = previousPathname && <button type="button" onClick={() => back()} aria-label="Go back to blogs" className="group mb-8 flex size-10 items-center justify-center rounded-full bg-white shadow-md ring-1 shadow-zinc-800/5 ring-zinc-900/5 transition lg:absolute lg:-left-5 lg:-mt-2 lg:mb-0 xl:-top-1.5 xl:left-0 xl:mt-0 dark:border dark:border-zinc-700/50 dark:bg-zinc-800 dark:ring-0 dark:ring-white/10 dark:hover:border-zinc-700 dark:hover:ring-white/20"><ArrowLeftIcon className="size-4 stroke-zinc-500 transition group-hover:stroke-zinc-700 dark:stroke-zinc-500 dark:group-hover:stroke-zinc-400" /></button>;
    $[17] = back;
    $[18] = previousPathname;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== blog.title) {
    t9 = <h1 className="mt-6 text-4xl font-semibold tracking-tight text-zinc-800 sm:text-5xl dark:text-zinc-100">{blog.title}</h1>;
    $[20] = blog.title;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = blog.date;
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="h-4 w-0.5 rounded-full bg-zinc-200 dark:bg-zinc-500" />;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== blog.date) {
    t12 = formatDate(blog.date);
    $[23] = blog.date;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12) {
    t13 = <span className="ml-3">{t12}</span>;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== blog.date || $[28] !== t13) {
    t14 = <time dateTime={t10} className="order-first flex items-center text-base text-zinc-400 dark:text-zinc-500">{t11}{t13}</time>;
    $[27] = blog.date;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14 || $[31] !== t9) {
    t15 = <header className="flex flex-col">{t9}{t14}</header>;
    $[30] = t14;
    $[31] = t9;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== children) {
    t16 = <Prose className="mt-8" data-mdx-content={true}>{children}</Prose>;
    $[33] = children;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <article>{t15}{t16}</article>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t17 || $[39] !== t8) {
    t18 = <div className="xl:relative"><div className="mx-auto max-w-2xl">{t8}{t17}</div></div>;
    $[38] = t17;
    $[39] = t8;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18 || $[42] !== t5 || $[43] !== t7) {
    t19 = <Container className="mt-16 lg:mt-32">{t5}{t7}{t18}</Container>;
    $[41] = t18;
    $[42] = t5;
    $[43] = t7;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  return t19;
}